import { Component } from '@angular/core';
import { NgClass } from '@angular/common';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { SharedService } from 'src/app/shared/services/shared.service';

@Component({
  selector: 'app-item-thumbnail',
  standalone: true,
  imports: [NgClass],
  template: `
    <div class="thumb-cell" [class.clickable]="imageUrl" (click)="openLightbox()">
      @if (loading) {
        <i class="fa-solid fa-spinner fa-spin thumb-icon"></i>
      } @else if (imageUrl) {
        <div class="thumb-frame">
          <img [src]="imageUrl" class="thumb-img" [ngClass]="{ hidden: !loaded }" (load)="loaded = true" (error)="onError()" />
        </div>
      } @else {
        <i class="fa-solid fa-image thumb-icon thumb-icon-empty"></i>
      }
    </div>
  `,
  styles: [
    `
      /* The host is inline and content-sized by default, so a narrow child (the
         placeholder icon) would sit at the left of the cell instead of centering. */
      :host {
        display: block;
        width: 100%;
        height: 100%;
        flex: 1 0 auto;
      }
      .thumb-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
      .thumb-cell.clickable {
        cursor: zoom-in;
      }
      /* Fixed size so every thumbnail gets the same visible top/bottom margin
         regardless of its own aspect ratio or whether the source photo already
         has a white background baked in — max-width/max-height directly on the
         <img> would size wide, short, or transparent images differently.
         Height is capped well under the grid's rowHeight (80px) so adjacent
         rows' thumbnails don't touch or overlap. */
      .thumb-frame {
        width: 120px;
        height: 70px;
        padding: 0px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        border-radius: 4px;
      }
      .thumb-img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }
      .thumb-img.hidden {
        display: none;
      }
      .thumb-icon {
        color: rgba(255, 255, 255, 0.25);
        font-size: 14px;
      }
      .thumb-icon-empty {
        color: rgba(255, 255, 255, 0.15);
      }
    `,
  ],
})
export class ItemThumbnailComponent implements ICellRendererAngularComp {
  imageUrl: string | null = null;
  loading = true;
  loaded = false;

  private thumbnailUrls: string[] = [];
  private componentParent: any;

  constructor(private sharedService: SharedService) {}

  agInit(params: ICellRendererParams): void {
    this.componentParent = params.context?.componentParent;
    this.load(params.data);
  }

  refresh(): boolean {
    return false;
  }

  onError(): void {
    // Broken/expired SAS URL — fall back to the empty-image icon rather than a broken <img>.
    this.imageUrl = null;
  }

  openLightbox(): void {
    if (!this.thumbnailUrls.length || !this.componentParent) return;
    this.componentParent.itemThumbnailUrls = this.thumbnailUrls;
    this.componentParent.currentImageIndex = 0;
    this.componentParent.toggleLightbox(true);
  }

  private load(data: any): void {
    if (!data || (!data.upc && !data.item_number && !data.mfg_part_number)) {
      this.loading = false;
      return;
    }

    this.sharedService
      .getInventoryThumbnailUrl(
        data.upc,
        data.item_number,
        data.manufacturer,
        data.mfg_part_number,
        data.origin,
        data.imageSource,
      )
      .subscribe((res) => {
        this.thumbnailUrls = res?.thumbnailUrls || [];
        this.imageUrl = this.thumbnailUrls[0] || null;
        this.loading = false;
      });
  }
}
